import React, { useState, useEffect, useRef } from 'react';
import { Search, Download, MapPin, Phone, Mail, Building, Briefcase, X, AlertCircle, Filter, RefreshCw, Globe, Clock, Database, Zap } from 'lucide-react';
import { searchBusinessLeads, validateLeads, Lead, LeadsResult, sortLeadsByPriority } from '../../lib/businessApiService';
import { useAuthStore } from '../../store/authStore';

interface AlternativeSearch {
  businessType: string;
  location: string;
  displayText: string;
}

// ✅ Interface atualizada com fromCache
interface LeadsResult {
  success: boolean;
  leads: Lead[];
  error?: string;
  executionTime: number;
  sources: string[];
  message?: string;
  fallbackUsed?: boolean;
  originalSearch?: {
    businessType: string;
    location: string;
  };
  suggestions?: AlternativeSearch[];
  fromCache?: boolean; // ✅ Nova propriedade para indicar se veio do cache
}

const LeadsSearch: React.FC = () => {
  const { user, subscription } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchResult, setSearchResult] = useState<LeadsResult | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [page, setPage] = useState(0);
  const [searchMessage, setSearchMessage] = useState<string>('');
  const [resultSummary, setResultSummary] = useState<string>('');
  
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (selectAll) {
      setSelectedLeads(leads.map((lead) => lead.id));
    } else {
      setSelectedLeads([]);
    }
  }, [selectAll, leads]);

  // ✅ Função handleSearch atualizada com cache
  const handleSearch = async (e?: React.FormEvent, customSearchTerm?: string, forceRefresh = false) => {
    if (e) e.preventDefault();
    
    const termToSearch = customSearchTerm || searchTerm;
    
    if (!termToSearch.trim()) {
      setError('Informe um setor e localização');
      return;
    }

    setLeads([]);
    setError('');
    setSearchPerformed(true);
    setIsLoading(true);
    setSearchMessage('🔍 Buscando estabelecimentos...');
    setResultSummary('');
    
    if (customSearchTerm) {
      setSearchTerm(customSearchTerm);
    }

    try {
      // ✅ MUDANÇA PRINCIPAL: forceRefresh agora é false por padrão
      const result = await searchBusinessLeads(
        termToSearch.trim(), 
        undefined, 
        forceRefresh, // ✅ Usa o parâmetro recebido (false por padrão)
        user?.id, 
        subscription
      );
      
      if (!result.success) throw new Error(result.error || 'Erro na busca');

      const validatedLeads = await validateLeads(result.leads);
      
      if (validatedLeads.length > 0) {
        setLeads(validatedLeads);
        setSearchResult(result);
        
        // ✅ Mensagem diferente para cache vs busca nova
        const cacheIndicator = result.fromCache ? ' (Cache)' : '';
        setSearchMessage(`Encontrados ${validatedLeads.length} estabelecimentos${cacheIndicator}:`);
        
        // ✅ Resultado summary com indicador de cache
        const sourceInfo = result.fromCache ? 'Cache Local' : (result.sources?.[0] || 'Busca Online');
        setResultSummary(`Busca concluída em ${(result.executionTime / 1000).toFixed(2)}s | Fonte: ${sourceInfo}`);
      } else {
        setSearchMessage('Nenhum estabelecimento encontrado. Tente uma busca mais ampla.');
      }
      
      setIsLoading(false);

    } catch (error: any) {
      setError(error.message || 'Erro na busca');
      setSearchMessage('Erro durante a busca. Tente novamente.');
      setIsLoading(false);
    }
  };

  const handleAlternativeSearch = (suggestion: AlternativeSearch) => {
    const newSearchTerm = `${suggestion.businessType} em ${suggestion.location}`;
    handleSearch(undefined, newSearchTerm, false); // ✅ false para permitir cache
  };

  const toggleLeadSelection = (id: string) => {
    setSelectedLeads(prev => 
      prev.includes(id) 
        ? prev.filter(leadId => leadId !== id)
        : [...prev, id]
    );
  };

  const exportToTxt = () => {
    if (selectedLeads.length === 0) {
      setError('Selecione pelo menos um lead para exportar.');
      return;
    }
    
    const leadsToExport = leads.filter(lead => selectedLeads.includes(lead.id));
    
    let content = `RELATÓRIO DE LEADS - ${new Date().toLocaleDateString('pt-BR')}\n`;
    content += `Busca: ${searchTerm}\n`;
    if (searchResult) {
      content += `Fontes: ${searchResult.sources.join(', ')}\n`;
      content += `Tempo de execução: ${(searchResult.executionTime / 1000).toFixed(2)}s\n`;
    }
    content += `Total de leads: ${leadsToExport.length}\n\n`;
    content += '='.repeat(50) + '\n\n';
    
    leadsToExport.forEach((lead, index) => {
      content += `LEAD ${index + 1}\n`;
      content += `Nome: ${lead.name}\n`;
      content += `Telefone: ${lead.phone}\n`;
      if (lead.email) {
        content += `Email: ${lead.email}\n`;
      }
      if (lead.address) {
        content += `Endereço: ${lead.address}\n`;
      }
      content += `Fonte: ${lead.source}\n`;
      content += `Busca: ${searchTerm}\n`;
      content += '-'.repeat(30) + '\n\n';
    });
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    const date = new Date().toISOString().split('T')[0];
    const searchSlug = searchTerm.replace(/\s+/g, '_').toLowerCase();
    a.download = `leads_${searchSlug}_${date}.txt`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    setPage(0);
    setSearchTerm('');
    setLeads([]);
    setError('');
    setSearchPerformed(false);
    setSelectedLeads([]);
    setSelectAll(false);
    setIsLoading(false);
    setSearchResult(null);
    setSearchMessage('');
    setResultSummary('');
    
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Busca de Leads Empresariais</h2>
      
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search-term" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Setor ou Categoria
            </label>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                id="search-term"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Ex: Restaurantes em Ituiutaba, Empresários em Uberlândia..."
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white pr-10"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
          
          <div className="md:w-auto flex items-end">
            <button
              type="submit"
              disabled={isLoading || !searchTerm.trim()}
              className={`w-full md:w-auto px-6 py-3 rounded-md text-white font-medium transition-colors ${
                isLoading || !searchTerm.trim() ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Buscando...
                </div>
              ) : (
                <div className="flex items-center">
                  <Search className="h-5 w-5 mr-2" />
                  Buscar
                </div>
              )}
            </button>
          </div>
        </div>
      </form>

      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-r-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <Database className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
              🏢 Sistema de Busca de Leads Empresariais Reais
            </h3>
            <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              <p><strong>Sistema de busca de leads reais usando múltiplas fontes de dados</strong></p>
              <p className="mt-2">Metodologia:</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li><strong>Base Local Verificada</strong> - Empresas reais cadastradas</li>
                <li><strong>Contatos Válidos</strong> - Dados oficiais de contatos</li>
                <li><strong>Padrões Regionais</strong> - Complemento baseado em dados reais</li>
                <li><strong>Validação Rigorosa</strong> - Apenas dados verificados</li>
                <li><strong>Múltiplas Fontes</strong> - Combinação de fontes confiáveis</li>
              </ul>
              <div className="mt-3 p-2 bg-blue-100 dark:bg-blue-800/30 rounded">
                <p className="text-xs font-medium">
                  <Zap className="inline h-3 w-3 mr-1" />
                  <strong>Funcionalidade:</strong> Sistema que busca leads reais de empresas ativas e verificadas.
                </p>
                <p className="text-xs mt-1">
                  <Globe className="inline h-3 w-3 mr-1" />
                  Os leads são obtidos de bases de dados reais e fontes oficiais.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        </div>
      )}
	  
      {isLoading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600 dark:text-gray-400">Buscando estabelecimentos, aguarde...</p>
        </div>
      )}

      {searchMessage && !isLoading && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded">
          <p className="text-blue-700 dark:text-blue-300 font-medium">{searchMessage}</p>
        </div>
      )}

      {/* ✅ Indicador visual de cache atualizado */}
      {resultSummary && leads.length > 0 && (
        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded flex items-center">
          {searchResult?.fromCache && <Database className="h-4 w-4 mr-2 text-green-500" />}
          {!searchResult?.fromCache && <Globe className="h-4 w-4 mr-2 text-blue-500" />}
          {resultSummary}
          {searchResult?.fromCache && <span className="ml-2 text-green-600 font-medium">(Cache)</span>}
        </div>
      )}

      {searchResult?.suggestions && searchResult.suggestions.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h3 className="text-lg font-semibold mb-3 text-gray-700 dark:text-gray-300">
            Você também pode buscar:
          </h3>
          <div className="flex flex-wrap gap-2">
            {searchResult.suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleAlternativeSearch(suggestion)}
                className="px-4 py-2 bg-blue-100 text-blue-700 dark:bg-blue-800/50 dark:text-blue-300 rounded-full hover:bg-blue-200 dark:hover:bg-blue-800/70 transition-colors text-sm font-medium"
              >
                {suggestion.displayText}
              </button>
            ))}
          </div>
        </div>
      )}
     
      {searchPerformed && leads.length > 0 && (
        <div className="mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Encontrados <span className="font-semibold">{leads.length}</span> leads para "{searchTerm}".
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={clearSearch}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Limpar busca
            </button>
            
            {/* ✅ Novo botão para atualizar/forçar refresh */}
            <button
              onClick={() => handleSearch(undefined, searchTerm, true)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Atualizar
            </button>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <Filter className="h-4 w-4 mr-1" />
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
            
            <button
              onClick={exportToTxt}
              disabled={selectedLeads.length === 0}
              className={`px-3 py-1 text-sm rounded-md flex items-center ${
                selectedLeads.length === 0
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <Download className="h-4 w-4 mr-1" />
              Exportar TXT ({selectedLeads.length})
            </button>
          </div>
        </div>
      )}
      
      {showFilters && leads.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Filtros por fonte</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {searchResult?.sources.map((source) => (
              <div key={source} className="flex items-center">
                <input
                  type="checkbox"
                  id={`filter-${source}`}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  defaultChecked
                />
                <label htmlFor={`filter-${source}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {source} ({leads.filter(lead => lead.source === source).length})
                </label>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {leads.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={() => setSelectAll(!selectAll)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Nome
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Telefone
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Endereço
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Fonte
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {leads.map((lead) => (
                <tr 
                  key={lead.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    selectedLeads.includes(lead.id) ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''
                  }`}
                >
                  <td className="px-3 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => toggleLeadSelection(lead.id)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center">
                      <Building className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {lead.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {lead.phone || 'Não informado'}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {lead.email || 'Não informado'}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="text-sm text-gray-500 dark:text-gray-300">
                        {lead.address || 'Não informado'}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lead.source === 'CNPJ.ws' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' :
                      lead.source === 'ReceitaWS' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' :
                      lead.source === 'Dados Locais Simulados' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300' :
                      lead.source === 'Busca Regional' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'
                    }`}>
                      {lead.source}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {searchPerformed && leads.length === 0 && !isLoading && !error && (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nenhum resultado encontrado</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Tente uma busca diferente ou verifique a ortografia.
          </p>
        </div>
      )}
      
      {!searchPerformed && !isLoading && (
        <div className="text-center py-12">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Busque leads empresariais reais</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Digite um setor ou categoria para buscar leads empresariais reais da sua região.
          </p>
          <div className="mt-6 max-w-md mx-auto">
            <div className="grid grid-cols-2 gap-4">
              {/* ✅ Cards de exemplo atualizados para não forçar refresh */}
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600" 
                   onClick={() => {
                     setSearchTerm('Restaurantes em Ituiutaba');
                     handleSearch(undefined, 'Restaurantes em Ituiutaba', false);
                   }}>
                <Briefcase className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Restaurantes</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                   onClick={() => {
                     setSearchTerm('Empresários em Uberlândia');
                     handleSearch(undefined, 'Empresários em Uberlândia', false);
                   }}>
                <Briefcase className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Empresários</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                   onClick={() => {
                     setSearchTerm('Advogados em Uberlândia');
                     handleSearch(undefined, 'Advogados em Uberlândia', false);
                   }}>
                <Briefcase className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Advocacia</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                   onClick={() => {
                     setSearchTerm('Clínicas em Ituiutaba');
                     handleSearch(undefined, 'Clínicas em Ituiutaba', false);
                   }}>
                <Briefcase className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Saúde</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                   onClick={() => {
                     setSearchTerm('Lojas em Uberlândia');
                     handleSearch(undefined, 'Lojas em Uberlândia', false);
					}}>
                <Briefcase className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Comércio</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                   onClick={() => {
                     setSearchTerm('Serviços em Ituiutaba');
                     handleSearch(undefined, 'Serviços em Ituiutaba', false);
                   }}>
                <Briefcase className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Serviços</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsSearch;